"use strict";
/**
 * Baseball Mode settings controls: factor slider, dollar-amount buttons,
 * event-type modal, settings tooltip, and initial state restoration.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupBaseballFactorControls = setupBaseballFactorControls;
exports.setupDollarAmountButtons = setupDollarAmountButtons;
exports.setupEventTypeModal = setupEventTypeModal;
exports.setupSettingsInfoTooltip = setupSettingsInfoTooltip;
exports.initBaseballState = initBaseballState;
const state_1 = require("../../../state");
const ui_1 = require("../../../ui");
const event_type_service_1 = require("../../../services/event-type-service");
const market_selection_service_1 = require("../../../services/market-selection-service");
const order_buttons_1 = require("../basketball/order-buttons");
const permissions_1 = require("../../../permissions");
const order_buttons_2 = require("../soccer/order-buttons");
const pricing_1 = require("./pricing");
// ---------------------------------------------------------------------------
// Baseball factor slider
// ---------------------------------------------------------------------------
/**
 * Update every sport's factor display for the shared factor value.
 * The factor is shared across sport modes, so both layouts' displays and
 * hints must track it regardless of which control changed it.
 */
function updateSharedFactorDisplays(factor) {
    (0, ui_1.updateBaseballFactorDisplay)(factor);
    (0, ui_1.updateBasketballFactorDisplay)(factor);
    (0, ui_1.updateSoccerFactorDisplay)(factor);
}
/**
 * Wire up a factor control row (slider and/or ±1 step buttons).
 * Both the baseball and basketball rows drive the same shared factor.
 */
function setupBaseballFactorControls(bbFactorSlider, bbFactorDecrementBtn, bbFactorIncrementBtn) {
    if (bbFactorSlider) {
        bbFactorSlider.addEventListener("input", (e) => {
            const newFactor = parseInt(e.target.value, 10);
            const state = (0, state_1.getState)();
            (0, state_1.updateState)({
                tradingSettings: {
                    ...state.tradingSettings,
                    baseballFactor: newFactor,
                },
            });
            (0, state_1.saveSettingsToStorage)();
            updateSharedFactorDisplays(newFactor);
        });
    }
    const updateFactorByStep = (delta) => {
        const state = (0, state_1.getState)();
        const current = state.tradingSettings.baseballFactor ?? 0;
        const newFactor = Math.max(0, Math.min(40, current + delta));
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                baseballFactor: newFactor,
            },
        });
        (0, state_1.saveSettingsToStorage)();
        updateSharedFactorDisplays(newFactor);
    };
    bbFactorDecrementBtn?.addEventListener("click", () => updateFactorByStep(-1));
    bbFactorIncrementBtn?.addEventListener("click", () => updateFactorByStep(1));
}
// ---------------------------------------------------------------------------
// Dollar amount buttons
// ---------------------------------------------------------------------------
/**
 * Wire up the ±$100 dollar-amount buttons.
 */
function setupDollarAmountButtons() {
    const dollarsUpBtn = document.getElementById("dollars-up");
    const dollarsDownBtn = document.getElementById("dollars-down");
    const primaryOrdersToggle = document.getElementById("primary-orders-toggle");
    const closeOppositeToggle = document.getElementById("close-opposite-toggle");
    const adjustDollars = (delta) => {
        const state = (0, state_1.getState)();
        const currentDollars = state.tradingSettings.dollarAmount;
        // Clamp to the tier's per-order cap so the committed amount can never
        // exceed it (server enforces regardless — this just keeps the UI honest).
        const newDollars = (0, permissions_1.clampBetToCap)(Math.max(1, currentDollars + delta));
        const updatedSettings = {
            ...state.tradingSettings,
            dollarAmount: newDollars,
        };
        (0, state_1.updateState)({ tradingSettings: updatedSettings });
        (0, state_1.saveSettingsToStorage)();
        (0, ui_1.updateSettingsDisplay)(updatedSettings);
    };
    dollarsUpBtn?.addEventListener("click", () => adjustDollars(100));
    dollarsDownBtn?.addEventListener("click", () => adjustDollars(-100));
    primaryOrdersToggle?.addEventListener("click", () => {
        const state = (0, state_1.getState)();
        const updatedSettings = {
            ...state.tradingSettings,
            primaryOrdersEnabled: state.tradingSettings.primaryOrdersEnabled === false,
        };
        (0, state_1.updateState)({ tradingSettings: updatedSettings });
        (0, state_1.saveSettingsToStorage)();
        (0, ui_1.updateSettingsDisplay)(updatedSettings);
    });
    closeOppositeToggle?.addEventListener("click", () => {
        const state = (0, state_1.getState)();
        const updatedSettings = {
            ...state.tradingSettings,
            closeOppositeOnBuy: state.tradingSettings.closeOppositeOnBuy === false,
        };
        (0, state_1.updateState)({ tradingSettings: updatedSettings });
        (0, state_1.saveSettingsToStorage)();
        (0, ui_1.updateSettingsDisplay)(updatedSettings);
    });
}
// ---------------------------------------------------------------------------
// Event type modal (including sport switch via indicator click)
// ---------------------------------------------------------------------------
/**
 * Wire up the event-type modal close/overlay and button handlers.
 * Also wire up the event type indicator click to open sport switch modal.
 */
function setupEventTypeModal() {
    const closeEventTypeBtn = document.getElementById("close-event-type-modal");
    const eventTypeOverlay = document.querySelector(".event-type-modal-overlay");
    if (closeEventTypeBtn) {
        closeEventTypeBtn.addEventListener("click", () => (0, ui_1.hideEventTypeModal)());
    }
    if (eventTypeOverlay) {
        eventTypeOverlay.addEventListener("click", (e) => {
            if (e.target === eventTypeOverlay)
                (0, ui_1.hideEventTypeModal)();
        });
    }
    // Event type button handlers
    document.querySelectorAll(".btn-event-type").forEach((btn) => {
        btn.addEventListener("click", () => {
            const eventType = btn.dataset
                .eventType;
            if (eventType) {
                (0, market_selection_service_1.handleEventTypeSelection)(eventType);
            }
        });
    });
    // Sport switch: clicking the event type indicator opens the modal
    const eventTypeIndicator = document.getElementById("event-type-indicator");
    if (eventTypeIndicator) {
        eventTypeIndicator.addEventListener("click", () => {
            (0, market_selection_service_1.openSportSwitchModal)();
        });
        // Keyboard accessibility: Enter or Space triggers sport switch
        eventTypeIndicator.addEventListener("keydown", (e) => {
            const key = e.key;
            if (key === "Enter" || key === " ") {
                e.preventDefault();
                (0, market_selection_service_1.openSportSwitchModal)();
            }
        });
    }
}
// ---------------------------------------------------------------------------
// Settings info tooltip
// ---------------------------------------------------------------------------
/**
 * Wire up the settings-info tooltip toggle on the order page.
 */
function setupSettingsInfoTooltip() {
    const settingsInfoBtn = document.getElementById("settings-info-btn");
    const settingsInfoPopup = document.getElementById("settings-info-popup");
    if (settingsInfoBtn && settingsInfoPopup) {
        settingsInfoBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            settingsInfoPopup.classList.toggle("hidden");
        });
        // Explicit close. The outside-click handler below would also catch
        // this, but only for as long as nothing between here and the document
        // stops propagation — and the popup's own rows already do that to stay
        // open when they are clicked. Owning the close keeps it working.
        const closeBtn = document.getElementById("settings-info-close-btn");
        closeBtn?.addEventListener("click", (e) => {
            e.stopPropagation();
            settingsInfoPopup.classList.add("hidden");
        });
        // Close popup when clicking anywhere else
        document.addEventListener("click", () => {
            if (settingsInfoPopup &&
                !settingsInfoPopup.classList.contains("hidden")) {
                settingsInfoPopup.classList.add("hidden");
            }
        });
    }
}
// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------
/**
 * Set the initial sport-mode UI state from persisted settings.
 * Call once during `setupOrderPage`.
 */
function initBaseballState() {
    const { tradingSettings, selectedEventTicker } = (0, state_1.getState)();
    const entry = selectedEventTicker
        ? (0, event_type_service_1.getEventType)(selectedEventTicker)
        : null;
    const eventType = entry?.type ?? null;
    (0, ui_1.updateOrderLayoutVisibility)(tradingSettings);
    updateSharedFactorDisplays(tradingSettings.baseballFactor ?? 0);
    (0, ui_1.updateEventTypeIndicator)(eventType);
    (0, ui_1.updateSlippageVisibility)(tradingSettings);
    // Initialise combined-mode (sport + manual price) display
    (0, ui_1.updateBaseballManualPriceDisplay)((0, ui_1.isCombinedPricingMode)(tradingSettings));
    if (tradingSettings.baseballMode === true) {
        (0, pricing_1.refreshAllHints)();
    }
    if (tradingSettings.basketballMode === true) {
        (0, order_buttons_1.refreshBasketballHints)();
    }
    if (tradingSettings.soccerMode === true) {
        (0, order_buttons_2.refreshSoccerHints)();
    }
    // Init offense/defense state if baseball
    if (entry && entry.type === "baseball") {
        (0, ui_1.updateHalfInningIndicator)(entry.halfInning);
        (0, ui_1.updateBaseballOffenseDefense)(entry.offenseTeam);
        (0, ui_1.updateResetOffenseVisibility)(entry.offenseTeam !== null);
    }
}
